<script setup>
const props = defineProps({
    results:           { type: Array,  default: () => [] },
    assessmentColumns: { type: Object, default: () => ({}) },
    subjectList:       { type: Array,  default: () => [] },
})

const performanceClass = (avg) => {
    if (avg === null || avg === undefined) return 'text-gray-400'
    if (avg >= 70) return 'text-green-600'
    if (avg >= 50) return 'text-amber-500'
    return 'text-red-500'
}

const CLASS_COLOURS = [
    'bg-primary-50 text-primary-700 border-primary-100',
    'bg-violet-50 text-violet-700 border-violet-100',
    'bg-rose-50 text-rose-700 border-rose-100',
    'bg-teal-50 text-teal-700 border-teal-100',
    'bg-orange-50 text-orange-700 border-orange-100',
    'bg-cyan-50 text-cyan-700 border-cyan-100',
    'bg-fuchsia-50 text-fuchsia-700 border-fuchsia-100',
    'bg-lime-50 text-lime-700 border-lime-100',
]

const classColourMap = {}

const classColour = (className) => {
    if (!classColourMap[className]) {
        const idx = Object.keys(classColourMap).length % CLASS_COLOURS.length
        classColourMap[className] = CLASS_COLOURS[idx]
    }
    return classColourMap[className]
}
</script>

<template>
    <div class="overflow-x-auto rounded-xl border border-gray-200 shadow-sm">
        <table class="w-full text-sm border-collapse min-w-max">

            <thead>
                <tr class="bg-gray-50 border-b border-gray-200">

                    <th
                        rowspan="2"
                        class="px-4 py-3 sticky left-0 bg-gray-50 z-20 border-r
                               text-left text-xs font-semibold text-gray-600 uppercase tracking-wider min-w-[220px]"
                    >
                        Student
                    </th>

                    <th
                        rowspan="2"
                        class="px-3 py-3 text-center text-xs font-semibold text-gray-600 uppercase tracking-wider
                               border-r border-gray-100 whitespace-nowrap"
                    >
                        Class
                    </th>

                    <template v-for="s in subjectList" :key="s.id">
                        <th
                            :colspan="assessmentColumns[s.id]?.length || 1"
                            class="px-2 py-3 text-center text-xs font-semibold text-gray-700
                                   uppercase tracking-wider border-x border-gray-100"
                        >
                            {{ s.name }}
                        </th>
                    </template>

                    <th
                        colspan="3"
                        class="px-4 py-3 text-center text-xs font-semibold text-gray-600
                               uppercase tracking-wider border-l border-gray-100"
                    >
                        Summary
                    </th>
                </tr>

                <tr class="bg-gray-100 border-b border-gray-200">
                    <template v-for="s in subjectList" :key="s.id">
                        <th
                            v-for="a in assessmentColumns[s.id]"
                            :key="a.id"
                            class="px-2 py-2 text-center text-xs font-medium text-gray-500"
                        >
                            {{ a.name }}
                        </th>

                        <th
                            v-if="!assessmentColumns[s.id]?.length"
                            class="px-2 py-2 text-center text-gray-300"
                        >
                            —
                        </th>
                    </template>

                    <th class="px-3 py-2 text-center text-xs font-semibold text-gray-600">
                        Total
                    </th>

                    <th class="px-3 py-2 text-center text-xs font-semibold text-gray-600">
                        Avg
                    </th>

                    <th class="px-3 py-2 text-center text-xs font-semibold text-gray-600">
                        Rank
                    </th>
                </tr>
            </thead>

            <tbody class="divide-y divide-gray-100">
                <tr
                    v-for="r in results"
                    :key="`${r.student.id}-${r.class_name}`"
                    :class="[
                        'transition-colors',
                        r.position === 1 ? 'bg-green-50/70'  :
                        r.position === 2 ? 'bg-amber-50/70'  :
                        r.position === 3 ? 'bg-orange-50/60' :
                        'hover:bg-gray-50',
                    ]"
                >

                    <td class="px-4 py-3 sticky left-0 bg-white z-10 border-r whitespace-nowrap">
                        <div class="flex flex-col">
                            <span class="text-sm font-medium text-gray-800">
                                {{ r.student.first_name }} {{ r.student.last_name }} {{ r.student.other_name ? r.student.other_name : '' }}
                            </span>

                            <span class="text-xs text-gray-400 uppercase tracking-wider">
                                {{ r.student.id }}
                            </span>
                        </div>
                    </td>

                    <td class="px-3 py-3 text-center border-r border-gray-100">
                        <span
                            :class="[
                                'inline-flex items-center px-2 py-0.5 rounded-full text-xs font-semibold border',
                                classColour(r.class_name)
                            ]"
                        >
                            {{ r.class_name }}
                        </span>
                    </td>

                    <template v-for="s in subjectList" :key="s.id">
                        <td
                            v-for="a in assessmentColumns[s.id]"
                            :key="a.id"
                            class="px-2 py-3 text-center text-sm text-gray-600"
                        >
                            {{ r.subject_scores[s.id]?.assessments[a.id]?.score ?? '—' }}
                        </td>

                        <td
                            v-if="!assessmentColumns[s.id]?.length"
                            class="px-2 py-3 text-center text-gray-300"
                        >
                            —
                        </td>
                    </template>

                    <td class="px-3 py-3 text-center">
                        <span class="inline-flex items-center px-2 py-0.5 text-xs font-semibold
                                     rounded-full bg-primary-50 text-primary border border-primary-100">
                            {{ r.total_score }}
                        </span>
                    </td>

                    <td class="px-3 py-3 text-center">
                        <span class="text-sm font-semibold" :class="performanceClass(r.average)">
                            {{ r.average }}
                        </span>
                    </td>

                    <td class="px-3 py-3 text-center">
                        <span
                            class="inline-flex items-center px-2 py-0.5 text-xs font-semibold rounded-full border"
                            :class="
                                r.position === 1 ? 'bg-green-50 text-green-700 border-green-100'
                              : r.position === 2 ? 'bg-amber-50 text-amber-700 border-amber-100'
                              : r.position === 3 ? 'bg-orange-50 text-orange-700 border-orange-100'
                              : 'bg-gray-50 text-gray-600 border-gray-200'
                            "
                        >
                            {{ r.position_text }}
                        </span>
                    </td>
                </tr>
            </tbody>
        </table>

        <div
            v-if="!results.length"
            class="flex flex-col items-center justify-center py-16 text-center"
        >
            <p class="text-sm text-gray-400">
                No results found for the selected filters.
            </p>
        </div>
    </div>
</template>